import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Sparkles, Star, Sun, Moon, CheckCircle2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingActions from '../components/FloatingActions';
import KundliCalculator from '../components/KundliCalculator';
import RelatedTools from '../components/RelatedTools';
import SEO from '../components/SEO';
import { useLang } from '../context/LanguageContext';

export default function KundliPage() {
  const { lang } = useLang();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [lang]);

  const features = lang === 'hi' ? [
    { icon: Star, title: 'सभी 9 ग्रहों की सटीक स्थिति', sub: 'Swiss Ephemeris (NASA/JPL grade)' },
    { icon: Sparkles, title: 'D1, D9, D10 कुंडली चार्ट', sub: 'राशि, नवांश, दशमांश' },
    { icon: Sun, title: 'विंशोत्तरी महादशा-अंतर्दशा-प्रत्यंतर', sub: 'तीन स्तर की दशा' },
    { icon: Moon, title: 'मांगलिक विश्लेषण + गोचर + फलादेश', sub: 'पराशरीय शास्त्र के श्लोकों सहित' },
  ] : [
    { icon: Star, title: 'All 9 Planet Positions', sub: 'Swiss Ephemeris (NASA/JPL grade)' },
    { icon: Sparkles, title: 'D1, D9, D10 Divisional Charts', sub: 'Rasi, Navamsa, Dashamsa' },
    { icon: Sun, title: 'Vimshottari MD-AD-PD', sub: 'Three-level dasha system' },
    { icon: Moon, title: 'Manglik + Transit + Predictions', sub: 'With authentic Parashari shlokas' },
  ];

  return (
    <div className="min-h-screen bg-cosmic-deep">
      <SEO
        title="Free Janam Kundli Online — जन्म कुंडली | ViratAstro"
        breadcrumbs={[
          { name: 'होम', path: '/' },
          { name: 'मुफ़्त कुंडली', path: '/kundli' },
        ]}
        description="Free online Janam Kundli with NASA-grade Swiss Ephemeris calculations — सभी 9 ग्रह, राशि चार्ट (D1), नवांश (D9), दशमांश (D10), विंशोत्तरी दशा, मांगलिक दोष विश्लेषण, गोचर फलादेश, और पराशरीय शास्त्र के श्लोकों सहित predictions। PDF download by Vivek Mudgal Astrologer."
        path="/kundli"
        image="/brand/og/va-og-kundli.png"
        keywords="free kundli, janam kundli, जन्म कुंडली, vedic horoscope, navamsa, dasamsa, mangal dosha, vimshottari dasha, parashari, swiss ephemeris, Vivek Mudgal"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'ViratAstro Free Janam Kundli Generator',
          alternateName: 'जन्म कुंडली Calculator',
          url: 'https://viratastro.cloud/kundli',
          description: 'NASA-grade Vedic Janam Kundli with Swiss Ephemeris — D1, D9, D10 charts, Vimshottari Dashas, Manglik & Parashari predictions. Free online.',
          applicationCategory: 'AstrologyApplication',
          operatingSystem: 'Web',
          browserRequirements: 'Requires JavaScript',
          inLanguage: ['hi-IN', 'en-IN'],
          offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'INR',
            availability: 'https://schema.org/InStock',
          },
          creator: {
            '@type': 'Person',
            name: 'Acharya Vivek Mudgal',
            url: 'https://viratastro.cloud/experts/vivek-virat-mudgal',
          },
          aggregateRating: {
            '@type': 'AggregateRating', ratingValue: '4.9', reviewCount: '287', bestRating: '5',
          },
          featureList: [
            'सभी 9 ग्रहों की सटीक स्थिति (Swiss Ephemeris)',
            'D1 · D9 (नवांश) · D10 (दशमांश) charts',
            'विंशोत्तरी महादशा-अंतर्दशा-प्रत्यंतर',
            'मांगलिक विश्लेषण + गोचर + फलादेश',
          ],
        }}
      />
      <Header />

      {/* Hero */}
      <section className="relative pt-28 pb-12 overflow-hidden">
        {/* Background glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-1/4 w-72 h-72 bg-mystic-gold/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-celestial-purple/15 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-mystic-gold/80 hover:text-mystic-gold mb-8 transition-colors text-sm"
            data-testid="kundli-back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            {lang === 'hi' ? 'होम पर वापस' : 'Back to Home'}
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-5 rounded-full bg-mystic-gold/10 border border-mystic-gold/30">
              <CheckCircle2 className="w-3.5 h-3.5 text-green-400" />
              <span className="text-xs text-mystic-gold font-medium tracking-wide">
                {lang === 'hi' ? 'NASA/JPL Grade · Swiss Ephemeris Powered' : 'NASA/JPL Grade · Swiss Ephemeris Powered'}
              </span>
            </div>

            <h1
              className="font-cormorant text-4xl sm:text-5xl lg:text-6xl text-mystic-gold mb-4 leading-tight"
              data-testid="kundli-page-h1"
            >
              {lang === 'hi' ? 'मुफ़्त वैदिक कुंडली' : 'Free Vedic Kundli'}
              <span className="block text-2xl sm:text-3xl text-divine-orange mt-2 font-normal">
                {lang === 'hi' ? 'जन्म कुंडली + विस्तृत फलादेश + PDF' : 'Birth Chart + Detailed Predictions + PDF'}
              </span>
            </h1>

            <p className="text-base text-mystic-gold/70 leading-relaxed max-w-2xl mx-auto mb-2">
              {lang === 'hi'
                ? 'अपनी जन्म तिथि, समय और स्थान दर्ज करें — सटीक वैदिक कुंडली, सभी 9 ग्रह, दशा, मांगलिक दोष विश्लेषण और पराशरीय शास्त्र के श्लोकों सहित विस्तृत फलादेश तुरंत प्राप्त करें।'
                : 'Enter your birth details and instantly get an accurate Vedic Kundli with all 9 planets, dasha periods, manglik analysis, and detailed predictions citing authentic Parashari shlokas.'}
            </p>
          </motion.div>

          {/* Feature pills */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-3 max-w-5xl mx-auto mt-10"
          >
            {features.map((f, i) => (
              <div
                key={i}
                className="bg-deep-violet/40 border border-mystic-gold/15 rounded-xl px-4 py-3 backdrop-blur-sm hover:border-mystic-gold/30 transition-all"
              >
                <f.icon className="w-5 h-5 text-mystic-gold mb-2" />
                <div className="text-sm font-semibold text-cream-soft leading-tight">{f.title}</div>
                <div className="text-xs text-mystic-gold/60 mt-1">{f.sub}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Calculator */}
      <KundliCalculator />

      {/* Trust strip */}
      <section className="py-12 bg-cosmic-deep border-t border-mystic-gold/10">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-cormorant text-2xl text-mystic-gold mb-4">
              {lang === 'hi' ? 'क्यों ViratAstro का Kundli Calculator?' : 'Why ViratAstro Kundli Calculator?'}
            </h2>
            <p className="text-cream-soft/80 text-sm leading-relaxed mb-8">
              {lang === 'hi'
                ? 'अधिकतर मुफ़्त online kundli simple formulas पर बनाई जाती हैं जिनमें 1-2 minute तक का error होता है। हमारा calculator Swiss Ephemeris (NASA/JPL ka standard library) use करता है — यानी astronomy-grade सटीकता। यही library professional astrologers, research institutions और सरकारी panchang use करते हैं।'
                : 'Most free online kundli generators use simplified formulas that can have 1-2 minute drift. Ours uses Swiss Ephemeris — the same NASA/JPL-grade library used by professional astrologers, research institutes and government panchang.'}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              {[
                { k: '33,000+', v: lang === 'hi' ? 'संतुष्ट ग्राहक' : 'Happy Clients' },
                { k: '33+', v: lang === 'hi' ? 'वर्षों का अनुभव' : 'Years Experience' },
                { k: '14+', v: lang === 'hi' ? 'देशों में सेवा' : 'Countries Served' },
                { k: '99.9%', v: lang === 'hi' ? 'गणना सटीकता' : 'Calc Accuracy' },
              ].map((s, i) => (
                <div key={i} className="bg-deep-violet/30 border border-mystic-gold/15 rounded-xl p-4">
                  <div className="font-cormorant text-3xl text-mystic-gold">{s.k}</div>
                  <div className="text-xs text-cream-soft/70 mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <RelatedTools />
      <Footer />
      <FloatingActions />
    </div>
  );
}
